import React, { useState, useEffect } from 'react';
import { Calculator, FileText, CheckSquare, Percent } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const FormCompletionRateCalculator = () => {
  const [formViews, setFormViews] = useState(1000);
  const [formSubmissions, setFormSubmissions] = useState(200);
  const [completionRate, setCompletionRate] = useState(0);

  useEffect(() => {
    calculateCompletionRate();
  }, [formViews, formSubmissions]);

  const calculateCompletionRate = () => {
    const rate = (formSubmissions / formViews) * 100;
    setCompletionRate(parseFloat(rate.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/form-completion-rate-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Form Completion Rate Calculator: Optimize Form Conversions | Free Tool</title>
        <meta name="description" content="Calculate your Form Completion Rate instantly. Get industry benchmarks, optimization tips, and strategies to improve form submissions and reduce abandonment." />
        <meta name="keywords" content="form completion rate calculator, form conversion rate, form optimization, form analytics, form abandonment" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Form Completion Rate Calculator: Boost Form Conversions" />
        <meta property="og:description" content="Calculate your Form Completion Rate instantly. Get actionable insights to improve form submissions and reduce abandonment." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Form Completion Rate Calculator: Boost Form Conversions" />
        <meta property="twitter:description" content="Calculate your Form Completion Rate instantly. Get actionable insights to improve form submissions and reduce abandonment." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Form Completion Rate Calculator",
            "description": "Free calculator to measure and optimize form completion rates",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Form Completion Rate",
              "Industry Benchmarks",
              "Form Optimization Tips",
              "Abandonment Analysis",
              "Conversion Strategies"
            ],
            "about": {
              "@type": "Thing",
              "name": "Form Completion Rate",
              "description": "The percentage of users who successfully complete and submit a form compared to the total number of users who viewed it."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Web Developers, UX Designers, Digital Marketers"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Form Completion Rate",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter total number of form views"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input number of form submissions"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your completion rate and optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Form Completion Rate Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Form Views</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FileText className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={formViews}
                  onChange={(e) => setFormViews(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="1000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Form Submissions</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <CheckSquare className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={formSubmissions}
                  onChange={(e) => setFormSubmissions(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="200"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            {completionRate}%
            <Percent className="w-8 h-8 ml-2 text-indigo-400" />
          </div>
          <p className="text-gray-600">Form Completion Rate</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          Your form completion rate is {completionRate}%. This means that {completionRate}% of users who view your form end up submitting it.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Form Type Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>Contact Forms: 15-25%</li>
          <li>Lead Generation Forms: 10-20%</li>
          <li>Newsletter Signup: 20-30%</li>
          <li>Event Registration: 15-25%</li>
          <li>Survey Forms: 10-15%</li>
          <li>Job Application Forms: 8-12%</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Form Optimization Strategies</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Keep forms short and focused</li>
          <li>Use clear and compelling CTAs</li>
          <li>Implement progress indicators</li>
          <li>Use inline validation</li>
          <li>Optimize for mobile devices</li>
          <li>Offer completion incentives</li>
          <li>Use conditional logic</li>
          <li>Test different layouts</li>
          <li>Minimize required fields</li>
          <li>Provide clear error messages</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Form Abandonment Factors</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Too many fields (27% abandonment)</li>
          <li>Unclear instructions (18% abandonment)</li>
          <li>Security concerns (15% abandonment)</li>
          <li>Technical issues (12% abandonment)</li>
          <li>Mobile incompatibility (10% abandonment)</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Impact Statistics</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Reducing form fields by 50% can increase conversions by 120%</li>
          <li>Mobile-optimized forms see 30% higher completion rates</li>
          <li>Auto-fill capabilities improve completion rates by 25%</li>
          <li>Progress bars increase completions by 40%</li>
          <li>Clear error messages reduce abandonment by 35%</li>
        </ul>
      </div>
    </div>
  );
};

export default FormCompletionRateCalculator;
